import { useTranslation } from 'react-i18next';
import { BookingCalendarLinks } from '../hooks/useBookingCalendarLinks';

interface AddToCalendarProps {
  links: BookingCalendarLinks;
  /** The Aperio preset styles it through the `apr-calendar*` hooks. */
  aperio?: boolean;
  /** Heading level of the section's title (fits the page's outline). */
  headingLevel?: 'h2' | 'h3' | 'h4';
  /** Extra class for the heading (the default page's subheader). */
  headingClassName?: string;
}

/**
 * "Add to calendar" on the confirmation page: Google Calendar's pre-filled
 * event, and the booking's .ics for Apple Calendar and Outlook (the same
 * event as the one in the confirmation email, so it never doubles up). It
 * brings no stylesheet of its own: the Aperio preset styles its `apr-*`
 * hooks in public/aperio/theme.css, and the default page lays it out inline,
 * so Go Oil's CSS bundle does not change.
 */
export default function AddToCalendar({
  links,
  aperio = false,
  headingLevel: Heading = 'h3',
  headingClassName,
}: AddToCalendarProps) {
  const { t } = useTranslation();
  const google = t('confirm.calendar.google');
  return (
    <section
      className={aperio ? 'apr-calendar' : undefined}
      aria-labelledby="add-to-calendar-title"
      data-testid="add-to-calendar"
    >
      <Heading
        id="add-to-calendar-title"
        className={aperio
          ? 'apr-confirm__h apr-calendar__title'
          : headingClassName}
      >
        {t('confirm.calendar.title')}
      </Heading>
      <ul
        className={aperio ? 'apr-calendar__links' : undefined}
        style={aperio ? undefined : {
          listStyle: 'none',
          padding: 0,
          margin: '0.5rem 0',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem 1.5rem',
        }}
      >
        <li>
          <a
            className={aperio ? 'apr-calendar__link' : undefined}
            href={links.googleCalendarUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${google} ${t('confirm.calendar.newTab')}`}
          >
            {google}
          </a>
        </li>
        <li>
          <a
            className={aperio ? 'apr-calendar__link' : undefined}
            href={links.icsUrl}
            download
          >
            {t('confirm.calendar.ics')}
          </a>
        </li>
      </ul>
      <p className={aperio ? 'apr-confirm__fine' : undefined}>
        {t('confirm.calendar.note')}
      </p>
    </section>
  );
}
